import { getData, postData } from './http';
import { getRootPath } from './rootPath';
import { logError } from './logging';
import { wrapNonArray } from './arrayUtils';
import { removeItem } from './desktop/localStorageProvider';
import * as currentUser from './currentUser';

const errorToIgnore = 'Station exists but recording not in progress';
const cardFeeOverridePermission = 59;
const root = getRootPath();
const debugApiUrl = `${root}website/debug`;
let paused = false;
let activeExtension;

export function loginOverride(username, password) {
    const parameters = $.param({
        Username: username,
        Password: password
    });

    return getData(`/api2/agents/get?${parameters}`, { logErrors: false })
        .then(function(agentInfo) {
            if (agentInfo.Permissions.indexOf(cardFeeOverridePermission) === -1) {
                return Promise.reject({ status: 401 });
            }
            return agentInfo;
        });
}

export function hasOverridePermission(agent) {
    if (!agent) { return false; }

    return agent.Permissions.indexOf(cardFeeOverridePermission) !== -1;
}

export function getBookingUserData(bookingId) {
    return getData(`/api2/bookings/getuserdata?BookingId=${bookingId}&DomainId=${TRPCONFIG.Domain.DomainId}`, { logErrors: false });
}

export function getBookingContact(bookingId) {
    return getData(`/api2/bookings/getbookingcontact?BookingId=${bookingId}&DomainId=${TRPCONFIG.Domain.DomainId}`, { logErrors: false });
}

export function signin(signinRequest) {
    return postData('/api2/agents/signin', signinRequest);
}

export function signout() {
    return postData('/api2/agents/signout').then(function() {
        try {
            removeItem('trackingTag');
        } catch (e) {
            // Storage cleanup must not prevent sign out.
        }

        currentUser.set(Object.assign({}, currentUser.get(), { Agent: null }));
    });
}

export function buildProvidersString(basket) {
    const providers = {};
    _.forEach(basket, entry => {
        wrapNonArray(entry).forEach(product => {
            if (!product || !product.CostBreakdown) { return; }

            for (let i = 0, ii = product.CostBreakdown.length; i < ii; i++) {
                const sp = product.CostBreakdown[i].SupplierName;
                if (sp) { providers[sp] = true; }
            }
        });
    });
    return Object.keys(providers).join(', ');
}

export function getAgentInformation() {
    return getData('/api2/agents/getsignedinuser', { logErrors: false });
}

export function setOfficeLocation(officeLocation) {
    return postData('/api2/agents/setcurrentofficelocation', { CurrentOfficeLocation: officeLocation }, { logErrors: false });
}

export function changePassword(oldPassword, newPassword) {
    return postData('/api2/agents/changepassword', { OldPassword: oldPassword, NewPassword: newPassword }, { logErrors: false });
}

export function resetPasswordInitiate(username) {
    return postData('/api2/agents/passwords/requestchange', { Username: username, CultureCode: TRPCONFIG.Domain.CultureCode, DomainId: TRPCONFIG.Domain.DomainId }, { logErrors: false });
}

export function resetPassword(newPassword, token) {
    return postData('/api2/agents/passwords/changewithtoken', { NewPassword: newPassword, Token: token }, { logErrors: false });
}

export function getCoworkers() {
    return getData('/api2/agents/getcoworkers', { logErrors: false });
}

export function getCreditCheck(customerId, amountRequested) {
    return postData('/api2/bookings/creditcheck', { CustomerId: customerId, AmountRequested: amountRequested }, { logErrors: false });
}

export function checkExtensionStatus(ext) {
    if (paused) {
        return Promise.reject(null);
    }
    if (ext == null) {
        return Promise.resolve(false);
    }
    const promises = [];

    for (let i = 0, ii = ext.length; i < ii; i++) {
        promises.push(_sendSabioCommand('Status', ext[i]));
    }

    return Promise.all(promises).then(function(extensionsStatus) {
        const activeExtensionStatus = extensionsStatus.filter(data => data.Error !== true)[0];
        if (activeExtensionStatus) {
            activeExtension = activeExtensionStatus.Extension;
        }

        return !!activeExtension;
    });
}

export function pauseCall() {
    if (paused || !activeExtension) {
        return Promise.reject('ABORT');
    }
    return _sendSabioCommand('Pause', activeExtension)
                .then(_errorInChain)
                .catch(_logError)
                .catch(_abortCallPauseProcess)
                .then(_togglePaused(true));
}

export function resumeCall() {
    if (!paused || !activeExtension) {
        return Promise.reject('ABORT');
    }
    return _sendSabioCommand('Resume', activeExtension)
            .then(_errorInChain)
            .catch(_logError)
            .catch(_abortCallPauseProcess)
            .then(_togglePaused(false))
            .then(_unsetActiveExtension);
}

export function getDebugSettings() {
    return getData(`${debugApiUrl}/getdebugsettings`, { logErrors: false });
}

export function updateDebugSettings(settings) {
    return postData(`${debugApiUrl}/setdebugcookie`, settings, { logErrors: false });
}

export function getSuppliers() {
    return getData('/api/search/suppliers');
}

export function logFatalError(type) {
    let errorMessage = `Call Pause Exception on extension ${
                        activeExtension
                        }: Fatal unable to ${
                        type}`;
    errorMessage += '| SiteName: MLT2';
    errorMessage += `| UserAgent: ${navigator.userAgent}`;

    logError(errorMessage);
}

function _sendSabioCommand(command, extension) {
    const parameters = {
        User: '',
        Host: '',
        Extension: extension,
        InteractionId: '',
        IpAdresses: '',
        Command: command,
        Sequence: ''
    };
    return postData(TRPCONFIG.Domain.SabioUrl, parameters, { logErrors: false }).then(data => Object.assign({}, data, { Extension: extension }));
}

// The server is not returning errors properly, this additional step let us force errors in the
// error chain, in order to handle them properly.
function _errorInChain(data) {
    if (data.Error === true) {
        return Promise.reject(data);
    }
    return data;
}

// If there is no active call we abort the whole process and return to the initial state.
function _abortCallPauseProcess(data) {
    if (data.ErrorMessage === errorToIgnore) {
        paused = false;
        activeExtension = undefined;
        return Promise.reject('ABORT');
    }
    return Promise.reject(data.ErrorMessage);
}

function _logError(data) {
    let errorMessage = `Call Pause Exception on extension ${activeExtension}: `;
    if (data) {
        errorMessage += data.ErrorMessage;
    } else {
        errorMessage += 'Unknown Error';
    }
    errorMessage += '| SiteName: MLT2';
    errorMessage += `| UserAgent: ${navigator.userAgent}`;
    logError(errorMessage);
    return Promise.reject(data);
}

function _togglePaused(val) {
    return function() {
        paused = val;
    };
}

function _unsetActiveExtension() {
    activeExtension = undefined;
}
